<script>
	import Store from 'kvstore';

	export default {
		props: {
			values: {
				type: Array
			},
			rangeMin: {
				type: Number,
				default: 0
			},
			rangeMax: {
				type: Number,
				default: 100
			},
			step: {
				type: Number,
				default: 1
			},
			label: {
				type: String
			},
			unit: {
				type: String
			},
			overflowUnit: {
				type: String
			},
			disabled: {
				type: Boolean
			}
		},
		data() {
			return {
				minValue: 0,
				maxValue: 100,
				guid: Store.inc('rangeSliderGuid')
			};
		},
		methods: {
			onInput() {
				let min = this.minValue;
				let max = this.maxValue;

				if (min > max) {
					max = min;
					min = this.maxValue;
				}

				this.$emit('range-change', [min, max]);
			}
		},
		computed: {
			cssProps() {
				const rangeMin = this.rangeMin;
				const denominator = this.rangeMax - rangeMin;
				const minPercentage = `${((100 * (this.minValue - rangeMin)) / denominator).toFixed(2)}%`;
				const maxPercentage = `${((100 * (this.maxValue - rangeMin)) / denominator).toFixed(2)}%`;

				return {
					'--min-percentage': minPercentage,
					'--max-percentage': maxPercentage
				};
			}
		},
		watch: {
			values: {
				handler(values) {
					const [min, max] = values || [];

					this.minValue = min || this.rangeMin;
					this.maxValue = typeof max === 'number' ? max : this.rangeMax;
				},
				immediate: true
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'mixins/global';

	.range-slider {
		--min-percentage: 0;
		--max-percentage: 100%;
		--track-color: #{$c-grey-light};
		--track-active-color: #{$c-grey};
		--thumb-size: 1.5rem;
		--thumb-background-color: #{$c-white};
		--thumb-border-color: #{$c-grey};
		--thumb-border-width: 1px;
		border: none;

		&.disabled {
			.range-label,
			.slider {
				cursor: not-allowed;
				opacity: 0.4;
			}
		}

		legend {
			font-weight: 700;
		}

		.label-container {
			display: flex;
			justify-content: space-between;
		}

		.range-label {
			color: inherit;
			margin: 0;
		}

		.slider-container {
			position: relative;
			display: flex;
			align-items: center;
			height: var(--thumb-size);
			margin: 0.25rem 0;
		}

		.slider {
			appearance: none;
			position: absolute;
			left: 0;
			width: 100%;
			cursor: pointer;

			@mixin slider-thumb {
				appearance: none;
				width: var(--thumb-size);
				height: var(--thumb-size);
				box-sizing: border-box;
				border-radius: 50%;
				background-color: var(--thumb-background-color);
				border: var(--thumb-border-width) solid var(--thumb-border-color);
				cursor: pointer;
				position: relative;
				z-index: 1;
			}

			&::-webkit-slider-thumb {
				@include slider-thumb;
			}

			&::-moz-range-thumb {
				@include slider-thumb;
			}

			&:focus {
				outline: none;

				&::-webkit-slider-thumb {
					@include focus-outline;
				}

				&::-moz-range-thumb {
					@include focus-outline;
				}
			}

			&:disabled {
				&::-webkit-slider-thumb {
					cursor: not-allowed;
				}

				&::-moz-range-thumb {
					cursor: not-allowed;
				}
			}

			@mixin slider-track {
				width: 100%;
				height: 3px;
				background: linear-gradient(
					to right,
					var(--track-color) 0% var(--min-percentage),
					var(--track-active-color) var(--min-percentage) var(--max-percentage),
					var(--track-color) var(--max-percentage)
				);
			}

			&.min {
				background: none;

				@supports not (-moz-appearance: none) {
					@include slider-track;
				}

				&::-moz-range-track {
					@include slider-track;
				}
			}

			&.max {
				background: none;
				--clickable-boundary: calc((var(--max-percentage) + var(--min-percentage)) / 2 - var(--thumb-size));
				clip-path: inset(-12px -6px -12px var(--clickable-boundary));
			}
		}
	}
</style>
<template>
	<fieldset
		class="range-slider"
		:class="{ disabled }"
		:style="cssProps">
		<legend
			v-if="label"
			class="range-slider-legend"
			v-html="label"></legend>
		<div class="label-container">
			<label
				:for="`range-slider-min-${guid}`"
				class="range-label min"
				>Min ({{ minValue }}{{ unit }})</label
			>
			<label
				:for="`range-slider-max-${guid}`"
				class="range-label max"
				>Max ({{ maxValue }}<span v-if="overflowUnit && maxValue === rangeMax">{{ overflowUnit }}</span
				>{{ unit }})</label
			>
		</div>
		<div class="slider-container">
			<input
				:id="`range-slider-min-${guid}`"
				class="slider min"
				type="range"
				:min="rangeMin"
				:max="rangeMax"
				:step="step"
				:disabled="disabled"
				v-model.number="minValue"
				@input="onInput" />
			<input
				:id="`range-slider-max-${guid}`"
				class="slider max"
				type="range"
				:min="rangeMin"
				:max="rangeMax"
				:step="step"
				:disabled="disabled"
				v-model.number="maxValue"
				@input="onInput" />
		</div>
	</fieldset>
</template>
